import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ShieldAlert, X, ArrowRight } from 'lucide-react';

/**
 * KYC Reminder Modal - Shows once per login session for users who haven't completed KYC
 * - Only shows for logged-in users with role !== 'admin'
 * - Only shows if kyc_status is not 'Verified' or 'Approved'
 * - Only shows on the Dashboard page
 * - Uses sessionStorage to ensure it only shows once per session
 */
const KycReminderModal = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        // Only show strictly on the main dashboard page
        if (location.pathname !== '/dashboard') {
            setShowModal(false);
            return;
        }

        // Check if we should show the modal
        if (!user) return;

        // Don't show for admin users
        if (user.role === 'admin') return;

        // DEBUG: console.log('KYC Check:', { is_verified: user.is_verified, kyc_status: user.kyc_status });

        // Don't show if KYC is already "done" (Verified/Approved/1) 
        // We check for both number and string '1' for is_verified
        // Comprehensive check for verification
        // We rely primarily on kyc_status being 'Verified' or 'Approved'
        const kycStatus = (user.kyc_status || '').toLowerCase();
        const isVerified = kycStatus === 'verified' || kycStatus === 'approved';
        const isPending = kycStatus === 'pending' || kycStatus === 'submitted';

        if (isVerified || isPending) {
            setShowModal(false);
            return;
        }

        // If kyc_status is completely missing from user object, it might be a stale or partial user object
        // Wait for fresh data if we don't even have the field (after fixing login.php this should be rare)
        if (user.kyc_status === undefined && user.is_verified === undefined) {
            return;
        }

        // Check if already shown this session
        const kycReminderShown = sessionStorage.getItem('kycReminderShown');
        if (kycReminderShown === 'true') return;

        // Show the modal with a slight delay for better UX
        const timer = setTimeout(() => {
            // Re-verify conditions inside timeout in case they changed or navigated away
            if (location.pathname === '/dashboard' && user) {
                setShowModal(true);
                // Mark as shown for this session
                sessionStorage.setItem('kycReminderShown', 'true');
            }
        }, 1500);

        return () => clearTimeout(timer);
    }, [user, location.pathname]);

    const handleClose = () => {
        setShowModal(false);
    };

    const handleGoToKyc = () => {
        setShowModal(false);
        navigate('/profile/kyc');
    };

    if (!showModal) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-fadeIn">
                {/* Header with gradient */}
                <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 text-white">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                                <ShieldAlert size={28} />
                            </div>
                            <h2 className="text-xl font-bold">
                                {user?.kyc_status === 'Rejected' ? 'KYC Rejected' : 'Complete Your KYC'}
                            </h2>
                        </div>
                        <button
                            onClick={handleClose}
                            className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    <p className="text-gray-600 mb-4">
                        {user?.kyc_status === 'Rejected'
                            ? "Your KYC documents were rejected by the administrator. Please re-submit valid documents to unlock all features."
                            : "Your KYC verification is pending. Please complete your KYC to unlock all features including:"}
                    </p>
                    <ul className="space-y-2 mb-6">
                        <li className="flex items-center gap-2 text-sm text-gray-700">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            Withdraw earnings to your bank account
                        </li>
                        <li className="flex items-center gap-2 text-sm text-gray-700">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            Access premium features
                        </li>
                        <li className="flex items-center gap-2 text-sm text-gray-700">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            Build trust with your network
                        </li>
                    </ul>

                    {/* Current Status */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 mb-6">
                        <p className="text-sm text-yellow-800">
                            <span className="font-semibold">Current Status:</span> {user?.kyc_status === 'Rejected' ? 'Rejected' : (user?.kyc_status || 'Not Submitted')}
                        </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                        <button
                            onClick={handleClose}
                            className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition"
                        >
                            Later
                        </button>
                        <button
                            onClick={handleGoToKyc}
                            className="flex-1 px-4 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-red-600 transition flex items-center justify-center gap-2"
                        >
                            {user?.kyc_status === 'Rejected' ? 'Re-submit KYC' : 'Complete KYC'} <ArrowRight size={18} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default KycReminderModal;
